import { useEffect, useState } from 'react'
import {
  Button,
  Dialog,
  Grid,
  IconButton,
  Typography,
  useMediaQuery,
  useTheme
} from '@material-ui/core'
import CheckCircleIcon from '@material-ui/icons/CheckCircle'
import CloseIcon from '@material-ui/icons/Close'
import { useLocation, useNavigate } from 'react-router'
import { useDispatch, useSelector } from 'react-redux'
import useAuth from '../../hooks/useAuth'
import ClioIntegrationTour from './ClioIntegrationTour'
import { getClio, getNylas } from '../../slices/accounts'
import { RootState } from '../../store'
import IntegrationSettingsTour from './IntegrationSettingsTour'

const IntroTour = () => {
  const theme = useTheme()
  const desktopSize = useMediaQuery(theme.breakpoints.up('lg'))

  const { user } = useAuth()

  const dispatch = useDispatch()
  const navigate = useNavigate()
  const location = useLocation()

  const [open, setOpen] = useState(false)
  const [clioTourComplete, setClioTourComplete] = useState(false)
  const [openClioTour, setOpenClioTour] = useState(false)

  const [integrationSettingsTourComplete, setIntegrationSettingsTourComplete] =
    useState(false)
  const [openIntegrationSettingsTour, setOpenIntegrationSettingsTour] =
    useState(false)

  const clio = useSelector((state: RootState) => state.account.clio)
  //   const nylas = useSelector((state: RootState) => state.account.nylas)

  useEffect(() => {
    if (!clio.data) {
      dispatch(getClio(user.auth0Token, user.accountId, user.australisId))
    }

    // if (!nylas.data) {
    //   dispatch(getNylas(user.auth0Token, user.accountId, user.australisId))
    // }

    const isIntroTourComplete = localStorage.getItem('introTourComplete')
    const isClioTourComplete = localStorage.getItem('clioTourComplete')
    const isIntegrationSettingsTourComplete = localStorage.getItem(
      'integrationSettingsTourComplete'
    )

    const currentPath = location.pathname

    if (currentPath === '/dashboard/overview') {
      setOpen(!isIntroTourComplete)
    } else if (currentPath === '/dashboard/settings/integrations') {
      if (
        !isIntegrationSettingsTourComplete &&
        clio?.data?.stats?.user_status === 'completed'
      ) {
        setOpenIntegrationSettingsTour(true)
      }
    }

    setClioTourComplete(!!isClioTourComplete)
    setIntegrationSettingsTourComplete(!!isIntegrationSettingsTourComplete)
  }, [])

  useEffect(() => {
    const isIntegrationSettingsTourComplete = localStorage.getItem(
      'integrationSettingsTourComplete'
    )

    const currentPath = location.pathname

    if (currentPath === '/dashboard/settings/integrations') {
      if (
        !isIntegrationSettingsTourComplete &&
        clio?.data?.stats?.user_status === 'completed'
      ) {
        setOpenIntegrationSettingsTour(true)
      }
    }
  }, [location.pathname])

  const handleIntroSkipButtonClick = () => {
    localStorage.setItem('introTourComplete', 'Y')
    setOpen(false)
  }

  const handleTourButtonClick = (tour: string) => {
    switch (tour) {
      case 'clio':
        setOpen(false)
        setOpenClioTour(true)

        break
      case 'integration':
        navigate('/dashboard/settings/integrations', { replace: true })
        setOpen(false)
        setInterval(() => {
          setOpenIntegrationSettingsTour(true)
        }, 1000)

        break
      default:
        break
    }
  }

  const setTourRun = (tour: string, value: boolean) => {
    switch (tour) {
      case 'clio':
        if (value) {
          setOpenClioTour(value)
        } else {
          localStorage.setItem('clioTourComplete', 'Y')
        }

        break
      case 'integration':
        if (value) {
          setOpenIntegrationSettingsTour(value)
        } else {
          localStorage.setItem('integrationSettingsTourComplete', 'Y')
        }

        break
      default:
        break
    }
  }

  return desktopSize ? (
    <>
      <Dialog
        open={open}
        PaperProps={{
          sx: {
            borderRadius: '4px',
            p: 6,
            width: '35vw',
            maxWidth: '35vw !important'
          }
        }}
      >
        <Grid container spacing={7}>
          <Grid item container justifyContent="space-between">
            <Grid item container spacing={2} xs={8}>
              <Grid item>
                <Typography variant="h4">
                  {`Welcome to Corvum${
                    user?.firstName ? `, ${user.firstName}` : ''
                  }!`}
                </Typography>
              </Grid>
              <Grid item xs={8}>
                <Typography>
                  We recommend that you review the following feature tour guides
                  that will help you get started with Corvum.
                </Typography>
              </Grid>
            </Grid>
            <Grid item>
              <IconButton onClick={() => setOpen(false)}>
                <CloseIcon fontSize="large" />
              </IconButton>
            </Grid>
          </Grid>
          <Grid item container flexDirection="column" spacing={2}>
            <Grid item>
              <Typography variant="h5">Quick tour</Typography>
            </Grid>
            <Grid
              item
              container
              justifyContent="space-between"
              alignItems="center"
            >
              <Grid item display="flex" alignItems="center">
                <CheckCircleIcon
                  sx={{
                    marginRight: 2,
                    color:
                      clioTourComplete ||
                      clio.data?.stats?.user_status === 'completed'
                        ? theme.palette.success.main
                        : '#A1A1A1'
                  }}
                />
                <Typography>Connect to Clio</Typography>
              </Grid>
              <Grid item xs={1}>
                <Button
                  variant={clioTourComplete ? 'outlined' : 'contained'}
                  onClick={() => handleTourButtonClick('clio')}
                  disabled={
                    clio.data?.stats?.user_status === 'completed' &&
                    !!clio.data?.clioId
                  }
                  fullWidth
                >
                  {clioTourComplete &&
                    clio.data?.stats?.user_status !== 'completed' &&
                    'Replay'}
                  {!clioTourComplete &&
                    clio.data?.stats?.user_status !== 'completed' &&
                    'Setup'}
                  {clio.data?.stats?.user_status === 'completed' &&
                    clio.data?.clioId &&
                    'Complete'}
                </Button>
              </Grid>
            </Grid>
            <Grid
              item
              container
              justifyContent="space-between"
              alignItems="center"
            >
              <Grid item display="flex" alignItems="center">
                <CheckCircleIcon
                  sx={{
                    marginRight: 2,
                    color: integrationSettingsTourComplete
                      ? theme.palette.success.main
                      : '#A1A1A1'
                  }}
                />
                <Typography>Integration Settings</Typography>
              </Grid>
              <Grid item xs={1}>
                <Button
                  variant={
                    integrationSettingsTourComplete ? 'outlined' : 'contained'
                  }
                  onClick={() => handleTourButtonClick('integration')}
                  disabled={
                    clio.data?.stats?.user_status !== 'completed' &&
                    !clio.data?.clioId
                  }
                  fullWidth
                >
                  {integrationSettingsTourComplete && 'Replay'}
                  {!integrationSettingsTourComplete && 'Setup'}
                </Button>
              </Grid>
            </Grid>
          </Grid>
          <Grid item container justifyContent="flex-end" spacing={2}>
            <Grid item>
              <Button onClick={handleIntroSkipButtonClick}>
                Don&apos;t show me this again
              </Button>
            </Grid>
          </Grid>
        </Grid>
      </Dialog>
      <ClioIntegrationTour run={openClioTour} setRun={setTourRun} />
      <IntegrationSettingsTour
        run={openIntegrationSettingsTour}
        setRun={setTourRun}
      />
    </>
  ) : null
}

export default IntroTour
